/**
 * Looking at a photograph before it is uploaded.
 *
 * A bad input photograph is the most expensive thing in this product. The
 * analyst reads it once and is confidently wrong about the fabric; every
 * frame of every roll then reproduces that wrong reading, and the critic
 * rejects them one at a time at a paid render each. Ten wasted frames, from
 * one blurred photograph taken in a dark room.
 *
 * None of that needs a model to catch. Blur, underexposure and blown
 * highlights are arithmetic over the pixels, and they are the three things
 * that actually go wrong when someone photographs a jacket on their bed with
 * a phone. The check runs in the browser, costs nothing, and warns rather
 * than blocks: it is advice from a light meter, not a gate, and the person
 * holding the camera can always see something the arithmetic cannot.
 */

export type CaptureIssue = 'soft' | 'dark' | 'bright' | 'small';

export interface CaptureReading {
  /** Relative acutance, 0-100. Below about 12 reads as out of focus. */
  sharpness: number;
  /** Mean brightness, 0-255. */
  brightness: number;
  /** Share of pixels at the very top of the range, 0-1. */
  clipped: number;
  /** The long edge, in pixels, as photographed. */
  longEdge: number;
  issues: CaptureIssue[];
}

/*
 * The lines, each chosen to sit well clear of the ordinary case rather than
 * at the edge of it. A warning that fires on a decent photograph is a warning
 * people learn to click past, which is worse than no warning.
 */
const SOFT_BELOW = 12;
const DARK_BELOW = 55;
const BRIGHT_ABOVE = 205;
const CLIPPED_ABOVE = 0.12;
const SMALL_BELOW = 640;

/**
 * Reads a photograph's focus and exposure from its pixels.
 *
 * Sharpness is the variance of a Laplacian over luma — the standard cheap
 * focus measure, and the reason it works is that a blurred image has no
 * sharp second derivatives anywhere. It is scaled so the numbers are
 * comparable between images of different sizes.
 */
export function inspectPixels(data: Uint8ClampedArray, width: number, height: number): CaptureReading {
  const pixels = width * height;
  const luma = new Float32Array(pixels);
  let sum = 0;
  let clipped = 0;

  for (let i = 0; i < pixels; i++) {
    const r = data[i * 4]!;
    const g = data[i * 4 + 1]!;
    const b = data[i * 4 + 2]!;
    // Rec. 601 luma: what "brightness" means to an eye, not the channel mean.
    const y = 0.299 * r + 0.587 * g + 0.114 * b;
    luma[i] = y;
    sum += y;
    if (y >= 250) clipped++;
  }

  const brightness = pixels ? sum / pixels : 0;

  let variance = 0;
  let count = 0;
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const i = y * width + x;
      const lap = 4 * luma[i]! - luma[i - 1]! - luma[i + 1]! - luma[i - width]! - luma[i + width]!;
      variance += lap * lap;
      count++;
    }
  }
  // The square root keeps the scale linear in contrast, and the divisor puts
  // an ordinary in-focus photograph somewhere around 20-60.
  const sharpness = count ? Math.min(100, Math.sqrt(variance / count) * 2) : 0;

  const issues: CaptureIssue[] = [];
  if (sharpness < SOFT_BELOW) issues.push('soft');
  if (brightness < DARK_BELOW) issues.push('dark');
  if (brightness > BRIGHT_ABOVE || clipped / Math.max(1, pixels) > CLIPPED_ABOVE) issues.push('bright');
  if (Math.max(width, height) < SMALL_BELOW) issues.push('small');

  return {
    sharpness: Math.round(sharpness),
    brightness: Math.round(brightness),
    clipped: pixels ? clipped / pixels : 0,
    longEdge: Math.max(width, height),
    issues,
  };
}

/**
 * Reads a file the same way, at a working size.
 *
 * The analysis runs on a downscaled copy: focus and exposure survive scaling,
 * a full-size phone photograph does not survive being read pixel by pixel in
 * the main thread, and the person is waiting. Anything unsupported returns
 * undefined, which every caller treats as "no opinion" rather than as a fault.
 */
export async function inspectImage(file: File, sampleEdge = 512): Promise<CaptureReading | undefined> {
  if (!file.type.startsWith('image/')) return undefined;
  try {
    const bitmap = await createImageBitmap(file);
    const long = Math.max(bitmap.width, bitmap.height);
    const scale = Math.min(1, sampleEdge / long);
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      bitmap.close();
      return undefined;
    }
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const reading = inspectPixels(ctx.getImageData(0, 0, width, height).data, width, height);
    // The size warning is about the photograph as taken, not as sampled.
    return { ...reading, longEdge: long, issues: sized(reading.issues, long) };
  } catch {
    return undefined;
  }
}

function sized(issues: CaptureIssue[], longEdge: number): CaptureIssue[] {
  const rest = issues.filter((issue) => issue !== 'small');
  return longEdge < SMALL_BELOW ? [...rest, 'small'] : rest;
}
